import * as yup from "yup";
const constants = require("domuso-constants");

export const reviewCCPaymentSchema = yup.object().shape({
  cof: yup.string(),
  cancellationRefund: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
});

export const autoPaymentSchema = yup.object().shape({
  cof: yup.string(),
  cancellationRefund: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
  returnWarning: yup.string().test("item must be checked", "Required", (val) => val === "checked"),
});

export const reviewCertifiedCCPaymentSchema = yup.object().shape({
  cof: yup.string(),
  cancellationRefund: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
  returnWarning: yup.string().test("item must be checked", "Required", (val) => val === "checked"),
});

export const reviewCertifiedBAPaymentSchema = yup.object().shape({
  cof: yup.string(),
  cancellationRefund: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
  returnWarning: yup.string().test("item must be checked", "Required", (val) => val === "checked"),
});

export const bankAccountNumberConfirmationSchema = yup.object().shape({
  manualBankAccountNumber: yup
    .string()
    .matches(/^[0-9]*$/, "Only numeric characters are allowed.")
    .matches(/^(?!0+$)/, "Account number cannot contain all zeroes.")
    .min(5, "Please enter at least 5 characters.")
    .max(17, "Please enter maximum of 17 characters.")
    .trim()
    .required("Account number is required")
    .nullable(),
  manualReBankAccountNumber: yup
    .string()
    .matches(/^[0-9]*$/, "Only numeric characters are allowed.")
    .matches(/^(?!0+$)/, "Account number cannot contain all zeroes.")
    .min(5, "Please enter at least 5 characters.")
    .max(17, "Please enter maximum of 17 characters.")
    .trim()
    .oneOf([yup.ref("manualBankAccountNumber"), undefined], "Account numbers do not match")
    .required("Re-Account number is required")
    .nullable(),
});

export const enterPaymentDetailsSchema = ({
  allowPartialRentPayments,
  balanceDue,
  currentPropertyFeeProfile,
}: any) => {
  const { non_certified_limit } = currentPropertyFeeProfile;
  return yup.object().shape({
    paymentAmount: yup
      .number()
      .required()
      .test("Min value validation", "Min value validation error", (value, testContext) => {
        if (value === 0) {
          return testContext.createError({ message: "Please enter an amount greater than $0" });
        }

        if (value == null) {
          return testContext.createError({ message: "Payment amount is required" });
        }

        if (!allowPartialRentPayments && value < balanceDue) {
          return testContext.createError({
            message:
              "Your property does not allow partial payments. Please pay your balance in full",
          });
        }

        return true;
      })
      .test(
        "non certified threshold validation",
        "non certified threshold error",
        (value, testContext) => {
          if (non_certified_limit && value * 100 > non_certified_limit) {
            return testContext.createError({
              message:
                "The amount entered exceeds the allowed limit. For further assistance, please contact your property manager.",
            });
          }
          return true;
        }
      ),
  });
};

export const reviewMobileCheckPaySchema = yup.object().shape({
  cof: yup.string(),
  confirmCheckDetailsMatch: yup
    .string()
    .test("item must be checked", "Required", (val) => val === "checked"),
  returnWarning: yup.string().test("item must be checked", "Required", (val) => val === "checked"),
});

export const checkInfoSchema = (limit: number) => {
  const { PERSONAL, CERTIFIED } = constants.check21.CheckType;
  return yup.object().shape({
    photoFront: yup.string().required("Front image required"),
    photoBack: yup.string().required("Back image required"),
    amount: yup
      .number()
      .required()
      .moreThan(0, "Amount must be greater than 0")
      .max(limit, `Amount must be less than $${limit}`),
    checkType: yup.mixed().oneOf([PERSONAL, CERTIFIED], "Please select a check type"),
    routingNumber: yup
      .string()
      .required("Routing number is required")
      .matches(/^\d+$/, "Routing number must only contain numbers")
      .test("AllZeroes", "Number cannot be all 0's", (val = "") => /[1-9]/.test(`${val}`))
      .test("9Digits", "Please enter 9 digits.", (val = "") => val?.length === 9),
    accountNumber: yup
      .string()
      .required("Account number is required")
      .matches(/^\d+$/, "Account number must only contain numbers")
      .test("AllZeroes", "Number cannot be all 0's", (val = "") => /[1-9]/.test(`${val}`))
      .min(5, "Account number must have at least 5 digits")
      .max(20, "Account number must have a maximum of 20 digits"),
  });
};

export const addCreditCardSchema = yup.object().shape({
  streetAddress: yup
    .string()
    .required("Address is Required")
    .test(
      "35Characters",
      "Please enter a maximum of 35 characters.",
      (val = "") => val.length < 36
    ),
  streetAddress2: yup.string(),
  cardholder: yup.string().required("Card Holder name is required"),
  cardNumber: yup.number().required("Card Number is required").typeError("Card Number is required"),
  expDate: yup
    .string()
    .required("Expiry date required")
    .test("expirationGreaterThanToday", "Can't be expired", (val) => {
      const monthInput = val?.slice(0, 2);
      const yearInput = val?.slice(2, 4);
      const month = parseInt(monthInput);
      const year = parseInt("20" + yearInput);

      const timeAtEntry = new Date();
      const monthAtEntry = timeAtEntry.getMonth() + 1;
      const yearAtEntry = timeAtEntry.getFullYear();

      if (year == yearAtEntry) {
        return month >= monthAtEntry;
      } else if (year > yearAtEntry) {
        return true;
      } else {
        return false;
      }
    }),
  cvv: yup
    .string()
    .required("CVV is required")
    .test("3Digits", "Cvv must have 3 digits", (val) => {
      if (!val) {
        return false;
      }
      if (val.toString().length == 3) {
        return true;
      }
      return false;
    }),
  state: yup.string().required("State is required"),
  city: yup.string().required("City is required"),
  zip: yup
    .string()
    .required("Zip is required")
    .matches(/^\d+$/, "Zip must only contain numbers")
    .min(5, "Zip length must be at least 5 characters long"),
});

export const verifyBillingAddressSchema = yup.object().shape({
  streetAddress: yup
    .string()
    .required("Address is Required")
    .test(
      "35Characters",
      "Please enter a maximum of 35 characters.",
      (val = "") => val.length < 36
    ),
  streetAddress2: yup.string(),
  state: yup.string().required("State is required"),
  city: yup.string().required("City is required"),
  zip: yup
    .string()
    .required("Zip is required")
    .matches(/^\d+$/, "Zip must only contain numbers")
    .min(5, "Zip length must be at least 5 characters long"),
});

export const addBankAccountSchema = yup.object().shape({
  accountHolder: yup.string().required("Account Holder is required"),
  accountType: yup.string().required("Account Type is required"),
  routingNumber: yup
    .string()
    .required("Routing number is required")
    .matches(/^\d+$/, "Routing number must only contain numbers")
    .test("9Digits", "Please enter 9 digits.", (val = "") => val?.length === 9),
  accountNumber: yup
    .string()
    .matches(/^\d+$/, "Account Number must only contain numbers")
    .min(5, "Account Number must have at least 5 digits")
    .max(20, "Account Number must have a maximum of 20 digits")
    .required("Account Number is required"),
  confirmAccountNumber: yup
    .string()
    .matches(/^\d+$/, "Account Number must only contain numbers")
    .test(
      "confirmAccountNumber",
      "Account Numbers do not match. Please re-enter.",
      // Relies on this don't use arrow function
      function (value: any) {
        const yupRef = yup.ref("accountNumber");
        return value === this.resolve(yupRef);
      }
    ),
});

export const autoPayDetailsSchema = ({ minimumDate, maximumDate }: any) => {
  return yup.object().shape({
    paymentDate: yup.date().required("Please select a date"),
  });
};
